import { MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useEvent, useStoreMap } from 'effector-react';
import classNames from 'classnames';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { UiLabel } from '@nx-frontend/shared/ui/label';
import { closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { UserPreview } from '@nx-frontend/shared/ui/user-preview';
import { getUserByIdParams } from '@nx-frontend/shared/store/users-store';
import { OrgMemberRole, OrgMemberRoleLabelLocKeyMap } from '@nx-frontend/shared/types';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { ContextMenuItemType } from '@nx-frontend/shared/types';
import { getUserDisplayName } from '@nx-frontend/shared/utils';

export enum AccessItems {
  NO_ACCESS = 'No access',
  ACCESS_GRANTED = 'Access granted',
  FULL_ACCESS = 'Full access',
  ONLY_SHARED = 'Only shared items',
}

const accessItemLocKeyMap: Record<AccessItems, string> = {
  [AccessItems.NO_ACCESS]: 'features.manage-members.access_options.no_access',
  [AccessItems.ACCESS_GRANTED]: 'features.manage-members.access_options.access_granted',
  [AccessItems.FULL_ACCESS]: 'features.manage-members.access_options.full_access',
  [AccessItems.ONLY_SHARED]: 'features.manage-members.access_options.only_shared_items',
};

export function OrgAccessItem({
  userId,
  initialAccessValue,
  accessValue,
  changeDisabled,
  roleRestricted,
  highlightedRole,
  role,
  portalId,
  onChange,
  isPortalItem = false,
  isLast,
  isOnlySharedAvailable,
}: {
  userId: number;
  initialAccessValue: AccessItems;
  accessValue: AccessItems;
  changeDisabled: boolean;
  roleRestricted: boolean;
  highlightedRole: boolean;
  role: OrgMemberRole;
  onChange: (value: AccessItems) => void;
  portalId?: string;
  isPortalItem?: boolean;
  isLast?: boolean;
  isOnlySharedAvailable?: boolean;
}) {
  const user = useStoreMap(getUserByIdParams(userId));
  const [openContextMenu, closeModal] = useEvent([openContextMenuEvent, closeModalEvent]);
  const t = useTranslations();
  if (!user) return;
  const userName = user ? getUserDisplayName(user, 0) : 'Anonymous User';

  const handleSelectClick = (event: MouseEvent) => {
    if (!changeDisabled) {
      const items =
        isPortalItem && initialAccessValue === AccessItems.NO_ACCESS
          ? [
              AccessItems.NO_ACCESS,
              AccessItems.FULL_ACCESS,
              ...(isOnlySharedAvailable ? [AccessItems.ONLY_SHARED] : []),
            ]
          : [AccessItems.NO_ACCESS, AccessItems.ACCESS_GRANTED];
      openContextMenu({
        items: items.map((access) => {
          return {
            title: t(accessItemLocKeyMap[access]),
            isChecked: accessValue === access,
            type: ContextMenuItemType.DEFAULT,
            action: () => {
              onChange(access);
            },
          };
        }),
        position: calculateContextMenuPosition(event.target, 4),
      });
    }
  };

  const restrictedTooltipContent = (
    <div>
      <span>
        Access for this role is restricted. <br />
        You can change it in the
        <Link
          href={`/portal/${portalId}/settings/access`}
          className="text-teal-500 hover:text-teal-400 ml-1 border-b border-teal-400"
          onClick={() => closeModal()}
        >
          Portal settings
        </Link>
        , or <br />
        alternatively, elevate the role for this user.
      </span>
    </div>
  );

  return (
    <div
      className={classNames('p-2 flex items-center ', {
        'border-b border-neutral-200': !isLast,
      })}
    >
      <div className="w-[65%]">
        <UserPreview name={userName} text={user.email} avatarSize="lg" avatarUrl={user.avatar}></UserPreview>
      </div>
      <div className="flex items-center w-[18%]">
        <UiLabel
          title={t(OrgMemberRoleLabelLocKeyMap[role])}
          className={classNames('text-center', highlightedRole ? 'bg-teal-100' : 'bg-neutral-100')}
        />
        <div className="ml-4 w-4 h-4 mr-2">
          {roleRestricted && (
            <Tooltip message={restrictedTooltipContent} position={UiTooltipPosition.LEFT}>
              <div className="cursor-pointer text-neutral-500">
                <Icon icon={IconsMap.INFO_FILL} />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="flex items-center w-[17%]">
        <div
          className={classNames('flex items-center p-2 text-xs w-[180px]', {
            'text-neutral-400': changeDisabled,
            'cursor-pointer': !changeDisabled,
          })}
          onClick={handleSelectClick}
        >
          <span>{t(accessItemLocKeyMap[accessValue])}</span>
          <span>
            <Icon className="w-4 h-4 ml-auto" icon={IconsMap.ARROW_DOWN} />
          </span>
        </div>
      </div>
    </div>
  );
}

export default OrgAccessItem;
